import {pushEcommerceEvent, pushEvent} from "@/lib/gtmDataLayer"

const PENDING_KEY = "braiv_checkout_pending"
const PURCHASE_DEDUPE_PREFIX = "ga4_purchase_logged_"
const AI_PACK_DEDUPE_PREFIX = "ga4_ai_pack_purchase_logged_"
const CANCEL_PREFIX = "ga4_subscription_cancel_logged_"
const LAST_STATUS_KEY_PREFIX = "braiv_last_sub_status_"

interface PendingCheckout {
    type: "subscription" | "ai_pack"
    priceId?: string
    productId?: string | null
    planName?: string
    billingCycle?: string
    packName?: string
    creditAmount?: number
    value?: number
    currency?: string
    timestamp?: number
}

interface SubscriptionLike {
    id?: string | null
    type?: string | null
    status?: string | null
    priceInterval?: string | null
}

function readPending(): PendingCheckout | null {
    if (typeof window === "undefined") return null
    try {
        const raw = window.sessionStorage.getItem(PENDING_KEY)
        if (!raw) return null
        return JSON.parse(raw) as PendingCheckout
    } catch {
        return null
    }
}

function clearPending(): void {
    if (typeof window === "undefined") return
    try {
        window.sessionStorage.removeItem(PENDING_KEY)
    } catch {
        // ignore
    }
}

function hasLogged(key: string): boolean {
    if (typeof window === "undefined") return true
    try {
        return window.localStorage.getItem(key) === "1"
    } catch {
        return false
    }
}

function markLogged(key: string): void {
    if (typeof window === "undefined") return
    try {
        window.localStorage.setItem(key, "1")
    } catch {
        // ignore
    }
}

function getLastStatus(subId: string): string | null {
    if (typeof window === "undefined") return null
    try {
        return window.localStorage.getItem(LAST_STATUS_KEY_PREFIX + subId)
    } catch {
        return null
    }
}

function setLastStatus(subId: string, status: string): void {
    if (typeof window === "undefined") return
    try {
        window.localStorage.setItem(LAST_STATUS_KEY_PREFIX + subId, status)
    } catch {
        // ignore
    }
}

export function processSubscriptionPurchase(subscription: SubscriptionLike | null | undefined): void {
    if (!subscription || !subscription.id) return
    const pending = readPending()
    if (!pending || pending.type !== "subscription") return

    const activeStatuses = ["active", "trialing"]
    if (!subscription.status || !activeStatuses.includes(subscription.status)) return

    const dedupeKey = `${PURCHASE_DEDUPE_PREFIX}${subscription.id}_${subscription.priceInterval || pending.billingCycle || "unknown"}`
    if (hasLogged(dedupeKey)) {
        clearPending()
        return
    }

    pushEcommerceEvent("purchase", {
        ecommerce: {
            transaction_id: subscription.id,
            currency: pending.currency || "USD",
            value: pending.value || 0,
            items: [{
                item_id: pending.priceId || subscription.id,
                item_name: pending.planName || subscription.type || "subscription",
                item_category: "subscription",
                item_variant: pending.billingCycle || subscription.priceInterval || "",
                price: pending.value || 0,
                quantity: 1,
            }],
        },
        plan_name: pending.planName || subscription.type || "",
        billing_cycle: pending.billingCycle || subscription.priceInterval || "",
        user_plan: subscription.type || "",
        user_billing_cycle: subscription.priceInterval || "",
    })

    markLogged(dedupeKey)
    clearPending()
}

export function processAiPackPurchase(billingCredits: number | null | undefined): void {
    if (billingCredits === null || billingCredits === undefined) return
    const pending = readPending()
    if (!pending || pending.type !== "ai_pack") return

    const stamp = pending.timestamp || Date.now()
    const dedupeKey = `${AI_PACK_DEDUPE_PREFIX}${pending.priceId || "unknown"}_${stamp}`
    if (hasLogged(dedupeKey)) {
        clearPending()
        return
    }

    pushEcommerceEvent("ai_pack_purchase", {
        ecommerce: {
            transaction_id: `ai_pack_${stamp}`,
            currency: pending.currency || "USD",
            value: pending.value || 0,
            items: [{
                item_id: pending.priceId || "",
                item_name: pending.packName || "ai_pack",
                item_category: "ai_pack",
                price: pending.value || 0,
                quantity: 1,
            }],
        },
        pack_name: pending.packName || "",
        credit_amount: pending.creditAmount || 0,
    })

    markLogged(dedupeKey)
    clearPending()
}

export function processSubscriptionCancel(subscription: SubscriptionLike | null | undefined): void {
    if (!subscription || !subscription.id || !subscription.status) return

    const subId = subscription.id
    const prev = getLastStatus(subId)
    const curr = subscription.status

    if (prev && prev !== curr) {
        const wasActive = prev === "active" || prev === "trialing"
        const nowCanceled = curr === "canceled" || curr === "incomplete_expired" || curr === "unpaid"
        if (wasActive && nowCanceled) {
            const key = `${CANCEL_PREFIX}${subId}_${curr}`
            if (!hasLogged(key)) {
                pushEvent("subscription_cancel", {
                    plan_name: subscription.type || "",
                    billing_cycle: subscription.priceInterval || "",
                    cancellation_reason: "not_specified",
                })
                markLogged(key)
            }
        }
    }

    setLastStatus(subId, curr)
}
